/**
 * EAA-1212: platform runtime configuration ("Cài đặt hệ thống") and the upload
 * limits every upload widget reads.
 *
 * The client's base URL already ends in `/api`, so paths here start at `/v1/…`
 * (see meAccess.service for the `/api/api/…` mistake that taught this).
 *
 * The portal consumes the same runtime-config API (EAA-1823) through its own client;
 * web-content may not import portal code, so the wire types are restated here.
 */

import { apiClient } from '@/services/api/client';

export type ConfigSource = 'default' | 'env' | 'override';

/** Editor type and bounds for one key. `type` is a stable wire name, not a Python repr. */
export interface ConfigSpec {
    type: string;
    min?: number;
    max?: number;
    max_length?: number;
    max_items?: number;
    format?: string;
    item_format?: string;
}

export interface SystemConfigKey {
    key: string;
    /** Effective value. `null` for a secret — never a masked secret. */
    value: unknown;
    /** What the service would use with no override; the "changed from" reference. */
    default: unknown;
    source: ConfigSource;
    editable: boolean;
    requires_restart: boolean;
    is_secret: boolean;
    /** Whether a secret has a value at all. */
    is_set?: boolean;
    /** Server-supplied display name (localised by Accept-Language); falls back to `key`. */
    label?: string;
    description?: string;
    spec: ConfigSpec | null;
    updated_by?: string;
    updated_at?: string | null;
}

export interface SystemConfigResponse {
    app_code: string;
    supported: boolean;
    overrides_readable?: boolean;
    generated_at: string;
    keys: SystemConfigKey[];
}

export interface StagedConfigChange {
    id: string;
    key: string;
    new_value: unknown;
    status?: 'pending' | 'confirmed' | 'expired';
    expires_at: string | null;
}

/**
 * The stage response plus the one-time confirmation token. The token is revealed
 * exactly once: hold it in component state only — never log it, never put it in a
 * query key, never persist it.
 */
export interface StagedConfigChangeWithToken extends StagedConfigChange {
    token: string;
}

export interface UploadLimitsResponse {
    media: {
        max_size_mb: number;
        extensions: Record<'image' | 'audio' | 'video' | 'document', string[]>;
    };
    document_max_size_mb: number;
    scorm_max_size_mb: number;
    h5p_max_size_mb: number;
    moodle_max_size_mb: number;
    ai_document_max_size_mb: number;
    model3d_max_size_mb: number;
    program_document_max_size_mb: number;
    import_max_files: number;
}

/**
 * EAA-1212: why the storage-space read has no space to show. `ok` is the only status
 * that carries a `space`; every other value is explained to the operator on its own.
 */
export type StorageSpaceStatus =
    | 'ok'
    | 'disabled'
    | 'not_configured'
    | 'no_tenant'
    | 'no_space'
    | 'unavailable';

/** One tenant's storage-api space, as the ACH backend relays it. Sizes are bytes. */
export interface StorageSpace {
    id: string;
    app_code: string;
    status: string;
    accepts_writes: boolean;
    quota_bytes: number | null;
    /** `null` = the app's default quota applies; a number = set per tenant in the portal. */
    quota_override_bytes: number | null;
    used_bytes: number;
    reserved_bytes: number;
    available_bytes: number | null;
    file_count: number;
}

export interface StorageSpaceResponse {
    status: StorageSpaceStatus;
    space: StorageSpace | null;
}

export const systemSettingsService = {
    async getConfig(): Promise<SystemConfigResponse> {
        const response = await apiClient.get<SystemConfigResponse>('/v1/config');
        return response.data;
    },

    /** Step 1 of 2. `value` must already be its real JSON type (the server type-checks it). */
    async stageChange(key: string, value: unknown): Promise<StagedConfigChangeWithToken> {
        const response = await apiClient.post<StagedConfigChangeWithToken>(
            '/v1/admin/config/stage',
            { key, value }
        );
        return response.data;
    },

    /** Step 2 of 2, with the one-time token from `stageChange`. */
    async confirmChange(changeId: string, token: string): Promise<StagedConfigChange> {
        const response = await apiClient.post<StagedConfigChange>(
            `/v1/admin/config/${encodeURIComponent(changeId)}/confirm`,
            { token }
        );
        return response.data;
    },

    /** Any authenticated user: the limits the upload widgets enforce client-side. */
    async getUploadLimits(): Promise<UploadLimitsResponse> {
        const response = await apiClient.get<UploadLimitsResponse>('/v1/config/upload-limits');
        return response.data;
    },

    /**
     * EAA-1212: the acting tenant's storage-api space (read-only). Platform staff send no
     * `X-Tenant-Id` (the client deliberately omits it, EAA-1886) and scope by
     * `?tenant_id=` instead, so the tenant goes in the query string.
     */
    async getStorageSpace(tenantId: string): Promise<StorageSpaceResponse> {
        const response = await apiClient.get<StorageSpaceResponse>(
            `/v1/config/storage-space?tenant_id=${encodeURIComponent(tenantId)}`
        );
        return response.data;
    },
};

export default systemSettingsService;
